import React, { useState, useRef, useEffect } from 'react';
import FileDropZone from '../shared/FileDropZone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUpload, faFileLines, faCheckCircle, faExclamationTriangle,
  faSpinner, faTimes, faDownload, faInfoCircle, faCloudArrowUp
} from '@fortawesome/free-solid-svg-icons';
import './CSVUploadModal.css';

const CSVUploadModal = ({
  isOpen,
  onClose,
  onUpload,
  title,
  templateColumns,
  templateExampleRow,
  templateNotes,
  entityRequired,
  entities,
  selectedEntity,
}) => {
  const [file, setFile] = useState(null);
  const [entityId, setEntityId] = useState(selectedEntity || '');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (selectedEntity) setEntityId(selectedEntity);
  }, [selectedEntity]);

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    validateAndSetFile(selected);
  };

  const validateAndSetFile = (selected) => {
    if (selected) {
      const ext = selected.name.split('.').pop().toLowerCase();
      if (!['csv', 'xlsx', 'xls'].includes(ext)) {
        alert('Please select a CSV or Excel file.');
        return;
      }
      setFile(selected);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    if (entityRequired && !entityId) {
      alert('Please select an entity first.');
      return;
    }

    setUploading(true);
    setResult(null);

    try {
      const data = await onUpload(file, entityId);
      setResult(data);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      const errData = error.response?.data;
      if (errData) {
        setResult(errData);
      } else {
        setResult({ error: 'Upload failed. Please check your file and try again.' });
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = () => {
    if (!templateColumns || templateColumns.length === 0) return;

    const lines = [templateColumns.join(';')];
    if (Array.isArray(templateExampleRow) && templateExampleRow.length > 0) {
      lines.push(templateExampleRow.join(';'));
    }
    const csvContent = lines.join('\n');
    const blob = new Blob(['\uFEFF' + csvContent + '\n'], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const safeName = (title || 'Upload').replace(/[^a-zA-Z0-9]/g, '_');
    link.download = `HMS_${safeName}_Template.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    setEntityId(selectedEntity || '');
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    return `${(bytes / 1024).toFixed(1)} KB`;
  };

  return (
    <div className="csv-modal-overlay" onClick={handleClose}>
      <div className="csv-modal" onClick={(e) => e.stopPropagation()}>

        <div className="csv-modal-header">
          <div className="csv-modal-header-text">
            <FontAwesomeIcon icon={faCloudArrowUp} className="csv-modal-header-icon" />
            <h2>{title || 'Upload CSV'}</h2>
          </div>
          <button className="csv-modal-close" onClick={handleClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="csv-modal-body">

          {entityRequired && (
            <div className="csv-form-group">
              <label className="csv-form-label">Target Entity</label>
              <select className="csv-form-select" value={entityId} onChange={(e) => setEntityId(e.target.value)}>
                <option value="">-- Select Entity --</option>
                {(entities || []).map(ent => (
                  <option key={ent.id} value={ent.id}>{ent.hms_entity_name}</option>
                ))}
              </select>
            </div>
          )}

          {templateColumns && templateColumns.length > 0 && (
            <div className="csv-template-section">
              <div className="csv-template-header">
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>CSV Format Requirements</span>
              </div>
              <p className="csv-template-desc">Your file must include these columns (semicolon-separated):</p>
              <div className="csv-template-columns">
                {templateColumns.map((col, i) => (
                  <span key={i} className="csv-column-tag">{col}</span>
                ))}
              </div>
              {Array.isArray(templateNotes) && templateNotes.length > 0 && (
                <ul className="csv-template-notes">
                  {templateNotes.map((note, i) => (
                    <li key={i}>{note}</li>
                  ))}
                </ul>
              )}
              <button type="button" className="csv-download-template-btn" onClick={handleDownloadTemplate}>
                <FontAwesomeIcon icon={faDownload} /> Download Template CSV
              </button>
            </div>
          )}

          <div className="csv-form-group">
            <label className="csv-form-label">Upload File</label>
            {/* Both doors — the file picker and the drop — go through
                validateAndSetFile, so they cannot disagree about which files
                are allowed. */}
            <FileDropZone
              className={`csv-dropzone ${file ? 'has-file' : ''}`}
              activeClassName="drag-over"
              onFiles={(files) => validateAndSetFile(files[0])}
              onError={(message) => alert(message)}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                hidden
              />
              {file ? (
                <div className="csv-file-info">
                  <FontAwesomeIcon icon={faFileLines} className="csv-file-icon" />
                  <div className="csv-file-details">
                    <span className="csv-file-name">{file.name}</span>
                    <span className="csv-file-size">{formatFileSize(file.size)}</span>
                  </div>
                  <button
                    className="csv-file-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              ) : (
                <div className="csv-dropzone-content">
                  <FontAwesomeIcon icon={faCloudArrowUp} className="csv-dropzone-icon" />
                  <span className="csv-dropzone-text">Drag & drop your file here</span>
                  <span className="csv-dropzone-hint">or click to browse -- CSV, XLSX, XLS</span>
                </div>
              )}
            </FileDropZone>
          </div>

          {result && (
            <div className="csv-result-section">
              {result.error && !result.errors && (
                <div className="csv-result-banner csv-result-error">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <span>{result.error}</span>
                </div>
              )}
              {!result.error && (
                result.created > 0 || result.updated > 0 ||
                (result.message && (!result.errors || result.errors.length === 0))
              ) && (
                <div className="csv-result-banner csv-result-success">
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <span>{result.message || `${result.created || 0} created, ${result.updated || 0} updated`}</span>
                </div>
              )}
              {result.errors && result.errors.length > 0 && (
                <div className="csv-result-errors">
                  <div className="csv-result-errors-header">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    <span>{result.errors.length} error(s) found:</span>
                  </div>
                  <div className="csv-result-errors-list">
                    {result.errors.map((err, i) => (
                      <div key={i} className="csv-error-row">
                        <span className="csv-error-row-num">Row {err.row}</span>
                        <span className="csv-error-field">{err.field}</span>
                        <span className="csv-error-msg">{err.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {result.warnings && result.warnings.length > 0 && (
                <div className="csv-result-warnings" style={{ marginTop: '12px', border: '1px solid #fcd34d', background: '#fffbeb', borderRadius: '8px', padding: '10px 12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#92400e', fontWeight: 500, marginBottom: '8px' }}>
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    <span>{result.warnings.length} warning(s) — rows were saved:</span>
                  </div>
                  <div className="csv-result-warnings-list" style={{ maxHeight: '160px', overflowY: 'auto' }}>
                    {result.warnings.map((w, i) => (
                      <div key={i} style={{ display: 'flex', gap: '10px', fontSize: '0.85em', padding: '3px 0', color: '#78350f' }}>
                        <span style={{ minWidth: '52px', color: '#92400e' }}>Row {w.row}</span>
                        <span>{w.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="csv-modal-footer">
          {result ? (
            <button type="button" className="csv-btn-upload" onClick={handleClose}>
              Done
            </button>
          ) : (
            <>
              <button type="button" className="csv-btn-cancel" onClick={handleClose}>Cancel</button>
              <button
                type="button"
                className="csv-btn-upload"
                onClick={handleUpload}
                disabled={!file || uploading || (entityRequired && !entityId)}
              >
                {uploading ? (
                  <><FontAwesomeIcon icon={faSpinner} spin /> Uploading...</>
                ) : (
                  <><FontAwesomeIcon icon={faUpload} /> Upload</>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CSVUploadModal;
